import React, { useState } from 'react';
import { Plus, Download, Upload, Save } from 'lucide-react';
import { useFunnelStore } from '../../store/funnelStore';
import { exportToJSON, exportToHTML } from '../../utils/export';
import { importFromJSON } from '../../utils/import';
import { validateFunnelForSave } from '../../utils/validation';
import SectionTree from '../Sections/SectionTree';
import AddSectionModal from '../Sections/AddSectionModal';

/**
 * Left Panel Component
 * Displays the section hierarchy and controls for managing sections
 */
const LeftPanel: React.FC = () => {
  const { funnel, updateFunnelName } = useFunnelStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [funnelName, setFunnelName] = useState(funnel.name);

  const handleExportJSON = () => {
    exportToJSON(funnel);
  };

  const handleExportHTML = () => {
    exportToHTML(funnel);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const importedFunnel = await importFromJSON(file);
          useFunnelStore.getState().setFunnel(importedFunnel);
          setFunnelName(importedFunnel.name);
        } catch (error) {
          alert('Failed to import funnel. Please check the file format.');
        }
      }
    };
    input.click();
  };

  const handleNameBlur = () => {
    if (funnelName.trim() !== funnel.name) {
      updateFunnelName(funnelName.trim() || 'Untitled Funnel');
    }
  };

  const handleSaveToLocalStorage = () => {
    const validation = validateFunnelForSave(funnel);
    
    if (!validation.valid) {
      alert(validation.errors.join('\n'));
      return;
    }

    localStorage.setItem('funnel-draft', JSON.stringify(funnel));
    alert('Funnel saved to browser storage!');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-600 uppercase mb-3">
          Funnel Structure
        </h2>
        
        {/* Funnel Name Input */}
        <input
          type="text"
          value={funnelName}
          onChange={(e) => setFunnelName(e.target.value)}
          onBlur={handleNameBlur}
          className="w-full px-3 py-2 text-sm border border-[#6F6F6F] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
          placeholder="Funnel name..."
        />

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#003C07] text-white text-sm rounded-[8px] hover:bg-[#003C07]/80 transition-colors"
            title="Add Section"
          >
            <Plus size={16} />
            <span>Add Section</span>
          </button>
        </div>

        {/* Export/Import Buttons */}
        <div className="grid grid-cols-1 gap-2 mt-2">
          <button
            onClick={handleSaveToLocalStorage}
            className="flex items-center justify-center  border border-[#6F6F6F] gap-1 px-2 py-1.5 text-gray-700 text-xs rounded-[7px] hover:bg-gray-200 transition-colors"
            title="Save Draft"
          >
            <Save size={14} />
            <span>Save</span>
          </button>
          <button
            onClick={handleImport}
            className="flex items-center justify-center border border-[#6F6F6F] gap-1 px-2 py-1.5  text-gray-700 text-xs rounded-[7px] hover:bg-gray-200 transition-colors"
            title="Import JSON"
          >
            <Upload size={14} />
            <span>Import</span>
          </button>
          <button
            onClick={handleExportJSON}
            className="flex items-center justify-center border border-[#6F6F6F] gap-1 px-2 py-1.5 text-gray-700 text-xs rounded-[7px] hover:bg-gray-200 transition-colors"
            title="Export as JSON"
          >
            <Download size={14} />
            <span>JSON</span>
          </button>
          <button
            onClick={handleExportHTML}
            className="flex items-center justify-center border border-[#6F6F6F] gap-1 px-2 py-1.5 text-gray-700 text-xs rounded-[7px] hover:bg-gray-200 transition-colors"
            title="Export as HTML"
          >
            <Download size={14} />
            <span>HTML</span>
          </button>
        </div>
      </div>

      {/* Section Tree */}
      <div className="flex-1 overflow-y-auto">
        <SectionTree />
      </div>

      {/* Add Section Modal */}
      {showAddModal && (
        <AddSectionModal onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
};

export default LeftPanel;

